import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {SingleResult, WeeklyMetricsGroups, WeeklyMetricsParams} from 'utils/types';
import {metricNamesByGroup} from 'utils/constants';
import {addResultCases} from 'utils/helpers';
import {WeeklyMetricsChartData} from 'services/item/slice';
import ApiService from 'redux/services/api-service';

export interface IState {
  loading: boolean;
  count: number;
  items: any[];
  areaVariableFilters: Record<string, string[]>;
  areaVariableFiltersLoading: boolean;
  modifyInProgress: boolean;
  avg_sales_per_area_per_week?: number;
  avg_hero_sales_per_area_per_week?: number;
  avg_halo_sales_per_area_per_week?: number;
  groupTotals: SingleResult<Record<string, number>>;
  [WeeklyMetricsGroups.area_weekly_average_sales]: SingleResult<WeeklyMetricsChartData>;
  [WeeklyMetricsGroups.area_weekly_total_sales]: SingleResult<WeeklyMetricsChartData>;
  [WeeklyMetricsGroups.area_weekly_num_stores_selling]: SingleResult<WeeklyMetricsChartData>;
}

export const areaEndpoints = {
  getAreas: createAsyncThunk('area/getAreas', async (params: any) => {
    const response = await ApiService.get(`/campaign_areas/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),
  deleteArea: createAsyncThunk('area/deleteItems', async (id: number) => {
    const response = await ApiService.delete(`/campaign_areas/${id}`);
    return response.data;
  }),
  getGroupTotals: createAsyncThunk('area/getGroupTotals', async (params: {campaign_id: number | string}) => {
    const response = await ApiService.get(
      `/campaign_areas/group_totals/?${new URLSearchParams({campaign_id: String(params.campaign_id)})}`,
    );
    return {value: response.data as Record<string, number>};
  }),
  getCampaignAreaAttributes: createAsyncThunk('area/getCampaignAreaAttributes', async (params: any) => {
    const response = await ApiService.get(`/campaign_area_variables/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),
  updateAreaBulk: createAsyncThunk('area/updateAreaBulk', async (payload: any) => {
    const response = await ApiService.post('/bulk_area_update/', payload);
    return response.data;
  }),
  getCampaignAreaVariableFilters: createAsyncThunk('area/getCampaignAreaVariableFilters', async (params: any) => {
    const response = await ApiService.get(`/campaign_area_variable_filters/?${new URLSearchParams(params).toString()}`);
    return response.data;
  }),
  updateAreaTestControl: createAsyncThunk('area/updateTestControl', async (payload: any) => {
    const response = await ApiService.post('/select_test_control/', payload);
    return response.data;
  }),
  getWeeklyMetricsChartDataAverageSales: createAsyncThunk(
    'area/getWeeklyMetricsChartDataAverageSales',
    async (params: WeeklyMetricsParams) => {
      const requestParams = {...params, name__in: metricNamesByGroup[WeeklyMetricsGroups.area_weekly_average_sales]};
      const response = await ApiService.get(`/weekly_metrics/chart_data/?${new URLSearchParams(requestParams as any)}`);
      return {as: 'success', value: response.data};
    },
  ),
  getWeeklyMetricsChartDataTotalSales: createAsyncThunk(
    'area/getWeeklyMetricsChartDataTotalSales',
    async (params: WeeklyMetricsParams) => {
      const requestParams = {...params, name__in: metricNamesByGroup[WeeklyMetricsGroups.area_weekly_total_sales]};
      const response = await ApiService.get(`/weekly_metrics/chart_data/?${new URLSearchParams(requestParams as any)}`);
      return {as: 'success', value: response.data};
    },
  ),
  getWeeklyMetricsChartDataNumStoresSelling: createAsyncThunk(
    'area/getWeeklyMetricsChartDataNumStoresSelling',
    async (params: WeeklyMetricsParams) => {
      const requestParams = {
        ...params,
        name__in: metricNamesByGroup[WeeklyMetricsGroups.area_weekly_num_stores_selling],
      };
      const response = await ApiService.get(`/weekly_metrics/chart_data/?${new URLSearchParams(requestParams as any)}`);
      return {as: 'success', value: response.data};
    },
  ),
};

export const areaSlice = createSlice({
  name: 'area',
  initialState: {
    loading: false,
    count: 0,
    items: [],
    areaVariableFilters: {},
    areaVariableFiltersLoading: false,
    modifyInProgress: false,
    groupTotals: {as: 'idle'},
    [WeeklyMetricsGroups.area_weekly_average_sales]: {as: 'idle', value: {}, error: null},
    [WeeklyMetricsGroups.area_weekly_total_sales]: {as: 'idle', value: {}, error: null},
    [WeeklyMetricsGroups.area_weekly_num_stores_selling]: {as: 'idle', value: {}, error: null},
  } as IState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(areaEndpoints.getAreas.fulfilled, (state, {payload}) => {
        state.loading = false;
        state.items = payload.results;
        state.count = payload.count;
        state.avg_sales_per_area_per_week = payload.avg_sales_per_area_per_week;
        state.avg_hero_sales_per_area_per_week = payload.avg_hero_sales_per_area_per_week;
        state.avg_halo_sales_per_area_per_week = payload.avg_halo_sales_per_area_per_week;
      })
      .addCase(areaEndpoints.getAreas.pending, state => {
        state.loading = true;
        state.items = [...state.items];
      })
      .addCase(areaEndpoints.deleteArea.fulfilled, (state, {payload}) => {
        const list = [...state.items];
        const index = list.findIndex(it => it.id === payload);
        if (index !== -1) {
          list.splice(index, 1);
        }
        state.items = list;
        state.count -= 1;
      })
      .addCase(areaEndpoints.updateAreaBulk.pending, state => {
        state.modifyInProgress = true;
      })
      .addCase(areaEndpoints.updateAreaBulk.fulfilled, state => {
        state.modifyInProgress = false;
      })
      .addCase(areaEndpoints.updateAreaBulk.rejected, state => {
        state.modifyInProgress = false;
      })
      .addCase(areaEndpoints.getCampaignAreaVariableFilters.pending, state => {
        state.areaVariableFiltersLoading = true;
      })
      .addCase(areaEndpoints.getCampaignAreaVariableFilters.fulfilled, (state, {payload}) => {
        state.areaVariableFiltersLoading = false;
        state.areaVariableFilters = payload;
      });
    addResultCases(builder, areaEndpoints.getGroupTotals, 'groupTotals');
    addResultCases(
      builder,
      areaEndpoints.getWeeklyMetricsChartDataAverageSales,
      WeeklyMetricsGroups.area_weekly_average_sales as string as keyof IState,
    );
    addResultCases(
      builder,
      areaEndpoints.getWeeklyMetricsChartDataTotalSales,
      WeeklyMetricsGroups.area_weekly_total_sales as string as keyof IState,
    );
    addResultCases(
      builder,
      areaEndpoints.getWeeklyMetricsChartDataNumStoresSelling,
      WeeklyMetricsGroups.area_weekly_num_stores_selling as string as keyof IState,
    );
  },
});

export const areaActions = areaSlice.actions;

export type AreaSlice = {
  [areaSlice.name]: ReturnType<(typeof areaSlice)['reducer']>;
};
